/**
 * scheduleCore — Sprint Scheduler v2 / S2.1.
 *
 * FONTE ÚNICA DE VERDADE para toda lógica de agenda compartilhada entre
 * a Página do Dispositivo (Aba Automação) e o Mapa/Fleet Explorer
 * (DeviceConfigModal / ScheduleEditor / painel direito).
 *
 * REGRA: nenhuma lógica de agenda pode ser duplicada fora daqui —
 *   - conversão de dias (weekday_mask ↔ days)
 *   - builder do payload config_schedule
 *   - validação por capability (regra v1/v2)
 *   - interpretação de capability do firmware
 *   - parser da agenda real (config_snapshot.schedule)
 *
 * Esta etapa (S2.1) NÃO altera execução do scheduler. Apenas unifica a UI
 * e prepara a leitura de capability (firmware ainda reporta v1).
 */
import { t } from "../i18n/index.js";

// ============================================================================
// Convenção de dias
// ============================================================================
// days[]      : 0=Dom .. 6=Sáb  (formato do backend ScheduleEntryIn.days e do
//               firmware config_schedule / config_snapshot)
// weekday_mask: bit0=Seg .. bit6=Dom  (schedule_templates do backend)
export const DAY_LABELS = ["D", "S", "T", "Q", "Q", "S", "S"];
export const DAY_NAMES  = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

/** weekday_mask (bit0=Seg..bit6=Dom) → days[] (0=Dom..6=Sáb). */
export function weekdayMaskToDays(mask) {
  const days = [];
  for (let b = 0; b < 7; b++) {
    if (mask & (1 << b)) days.push((b + 1) % 7);
  }
  return days;
}

/** days[] (0=Dom..6=Sáb) → weekday_mask (bit0=Seg..bit6=Dom). */
export function daysToWeekdayMask(days) {
  let mask = 0;
  for (const d of days || []) {
    const n = Number(d);
    if (n >= 0 && n <= 6) mask |= (1 << ((n + 6) % 7));
  }
  return mask;
}

// Duração padrão do pulso (relay_reboot) quando não especificada.
// Valor único compartilhado (antes: 30 no editor do Mapa, 10 no doSync).
export const SCHEDULE_PULSE_DEFAULT_S = 30;

// ============================================================================
// Helpers de entry (movidos do ScheduleEditor — fonte única)
// ============================================================================
export function newOnOffEntry() {
  return {
    days: [1, 2, 3, 4, 5],
    on_time: "08:00",
    off_time: "18:00",
    action: "relay_on",
    enabled: true,
  };
}

export function newPulseEntry() {
  return {
    days: [0, 1, 2, 3, 4, 5, 6],
    time: "04:30",
    duration_s: SCHEDULE_PULSE_DEFAULT_S,
    action: "relay_reboot",
    enabled: true,
  };
}

export function isPulseEntry(entry) {
  return !!(entry && entry.time !== undefined && entry.duration_s !== undefined);
}

/** ACT-2-NOC — entry DISCRETA de liga/desliga: `time` com action relay_on/off
 *  (duração não se aplica). Executada pelo scheduler v2 (firmware ACT-1);
 *  autoria gated por capability multi_event (prop `allowSwitchActions` do
 *  editor) e por guard 409 no backend. */
export function isDiscreteSwitchEntry(entry) {
  return !!(
    entry &&
    entry.time !== undefined &&
    (entry.action === "relay_on" || entry.action === "relay_off")
  );
}

/** Normaliza um entry antes de enviar ao backend (pulso × discreto × janela). */
export function sanitizeEntry(entry) {
  if (!entry) return null;
  const e = { ...entry };
  if (isDiscreteSwitchEntry(e)) {
    // ACT-2-NOC — discreto: só time+action; duração e janela não se aplicam.
    delete e.on_time;
    delete e.off_time;
    delete e.duration_s;
  } else if (isPulseEntry(e)) {
    delete e.on_time;
    delete e.off_time;
    if (e.action !== "relay_reboot" && e.action !== "esp_reboot") {
      e.action = "relay_reboot";
    }
    e.duration_s = (() => {
      const n = Number(e.duration_s);
      if (!Number.isFinite(n)) return SCHEDULE_PULSE_DEFAULT_S;
      return Math.max(1, Math.min(300, Math.round(n)));
    })();
  } else {
    delete e.time;
    delete e.duration_s;
    if (e.action !== "relay_on" && e.action !== "relay_off") {
      e.action = "relay_on";
    }
  }
  e.enabled = e.enabled !== false;
  if (!Array.isArray(e.days)) e.days = [];
  return e;
}

/** Validação client-side de um entry (casa com ScheduleEntryIn._validate_mode). */
export function validateEntry(entry, idx = 0) {
  if (!entry) return null;
  const label = `#${idx + 1}`;
  if (!Array.isArray(entry.days) || entry.days.length === 0) {
    return `${label}: ${t("templateBuilder.err.daysRequired")}`;
  }
  if (isDiscreteSwitchEntry(entry)) {
    // ACT-2-NOC — discreto: exige só horário (duração não se aplica).
    if (!entry.time) return `${label}: ${t("templateBuilder.err.pulseTimeRequired")}`;
  } else if (isPulseEntry(entry)) {
    if (!entry.time) return `${label}: ${t("templateBuilder.err.pulseTimeRequired")}`;
    const dur = Number(entry.duration_s);
    if (!Number.isFinite(dur) || dur < 1 || dur > 300) {
      return `${label}: ${t("templateBuilder.err.pulseDurationRange")}`;
    }
    if (entry.action !== "relay_reboot" && entry.action !== "esp_reboot") {
      return `${label}: ${t("templateBuilder.err.pulseActionInvalid")}`;
    }
  } else {
    if (!entry.on_time) return `${label}: ${t("templateBuilder.err.onTimeRequired")}`;
    if (!entry.off_time) return `${label}: ${t("templateBuilder.err.offTimeRequired")}`;
    if (entry.on_time === entry.off_time) {
      return `${label}: ${t("templateBuilder.err.onOffEqual")}`;
    }
    if (entry.action === "relay_reboot" || entry.action === "esp_reboot") {
      return `${label}: ${t("templateBuilder.err.onOffActionInvalid")}`;
    }
  }
  return null;
}

/** Validação de todo o payload (enabled + entries). */
export function validateSchedule({ enabled, entries }) {
  if (!enabled) return null;
  if (!Array.isArray(entries) || entries.length === 0) {
    return t("templateBuilder.err.scheduleNoEntries");
  }
  for (let i = 0; i < entries.length; i++) {
    const err = validateEntry(entries[i], i);
    if (err) return err;
  }
  return null;
}

// ============================================================================
// Builder ÚNICO do payload config_schedule
// ============================================================================
/**
 * Monta o body do comando config_schedule a partir de entries canônicos
 * ({days, time|on_time/off_time, duration_s?, action, enabled?}).
 * Sanitiza, remove entries sem dias, e deriva `enabled`.
 *
 * `enabled` opcional: quando passado (toggle explícito do modal), é
 * respeitado — mas forçado a false se não houver entries válidos (o
 * firmware rejeita enabled=true sem entries). Sem ele, deriva de
 * entries.length > 0 (caminho da Aba Automação).
 */
export function buildConfigSchedulePayload({ entries, exceptions = [], enabled }) {
  const clean = (entries || [])
    .map(sanitizeEntry)
    .filter(Boolean)
    .filter((e) => Array.isArray(e.days) && e.days.length > 0);
  const cleanExceptions = (exceptions || []).filter(
    (d) => /^\d{4}-\d{2}-\d{2}$/.test(d || ""),
  );
  const derived = clean.length > 0;
  const finalEnabled = (typeof enabled === "boolean") ? (enabled && derived) : derived;
  return {
    enabled: finalEnabled,
    entries: clean,
    exceptions: cleanExceptions,
  };
}

// ============================================================================
// Capability do firmware (contrato S2.1)
// ============================================================================
export const DEFAULT_CAPABILITY = {
  version: 1,
  multi_event: false,
  max_entries: 1,
  source: "firmware_v1",
};

/**
 * Interpreta a capability do scheduler do firmware a partir do objeto do
 * device (heartbeat / snapshot / DTO do fleet). Tolerante a múltiplos
 * locais — funciona assim que o backend (S2.6) expor os campos.
 * Fallback seguro = v1 single-event quando ausente.
 */
export function getSchedulerCapability(deviceData) {
  const d = deviceData || {};
  const hb = d.heartbeat || d.last_hb_payload || {};
  const snap = d.last_config_snapshot?.schedule || d.schedule || {};
  const pick = (k) => d[k] ?? hb[k] ?? snap[k];

  const version = Number(pick("scheduler_version"));
  if (!Number.isFinite(version) || version < 1) {
    return { ...DEFAULT_CAPABILITY };
  }
  const multi = pick("scheduler_multi_event");
  const maxE = Number(pick("scheduler_max_entries"));
  return {
    version,
    multi_event: multi === true || (multi == null && version >= 2),
    max_entries: Number.isFinite(maxE) && maxE > 0
      ? maxE
      : (version >= 2 ? 8 : 1),
    source: pick("scheduler_source") || (version >= 2 ? "firmware_v2" : "firmware_v1"),
  };
}

// ============================================================================
// Validação por capability — regra v1/v2 ÚNICA
// ============================================================================
/**
 * Aplica a regra do firmware aos entries ATIVOS. Retorna {ok, reason}.
 *  - v1 / multi_event=false → no máximo 1 evento ativo.
 *  - v2 → no máximo `max_entries` eventos ativos.
 */
export function validateScheduleForCapability(entries, capability) {
  const cap = capability || DEFAULT_CAPABILITY;
  const active = (entries || []).filter((e) => e && e.enabled !== false);
  if (!cap.multi_event && active.length > 1) {
    return {
      ok: false,
      reason:
        "Firmware atual suporta apenas 1 agenda ativa. "
        + "Desative os outros eventos antes de sincronizar.",
    };
  }
  if (cap.max_entries && active.length > cap.max_entries) {
    return {
      ok: false,
      reason: `Firmware suporta no máximo ${cap.max_entries} eventos ativos.`,
    };
  }
  return { ok: true, reason: null };
}

// ============================================================================
// Parser ÚNICO da agenda real (config_snapshot.schedule)
// ============================================================================
/**
 * Normaliza o bloco schedule do snapshot do firmware. Aceita tanto o objeto
 * `schedule` direto quanto o snapshot inteiro. Retorna apenas entries
 * reportados pelo firmware (source firmware_v1/v2).
 */
export function parseRealSchedule(snapshotOrSchedule) {
  const sch = snapshotOrSchedule?.schedule || snapshotOrSchedule || {};
  const all = Array.isArray(sch.entries) ? sch.entries : [];
  const entries = all.filter(
    (e) => e?.source === "firmware_v1" || e?.source === "firmware_v2",
  );
  return {
    enabled: sch.enabled === true,
    version: sch.scheduler_version ?? null,
    source: sch.scheduler_source ?? (entries[0]?.source ?? null),
    entries,
  };
}

// ============================================================================
// S2.6 — Parser do bloco schedule_v2 (DORMENTE) — fonte única de verdade
// ============================================================================
/**
 * Normaliza o bloco `schedule_v2` do snapshot do firmware. Aceita o snapshot
 * inteiro (`.schedule_v2`), o `last_config_snapshot`, ou o próprio objeto
 * `schedule_v2`. Retorna null se ausente.
 *
 * IMPORTANTE: o V2 é PERSISTIDO mas NÃO EXECUTA nesta fase. Este parser NÃO
 * expõe next_run/última execução de propósito — esses eventos não disparam.
 * O scheduler ativo continua sendo o v1 (campo `activeScheduler`).
 */
export function parseScheduleV2(input) {
  if (!input || typeof input !== "object") return null;
  const sv2 =
    input.schedule_v2
    || input.last_config_snapshot?.schedule_v2
    || ((input.scheduler_state !== undefined
         || input.execution_enabled !== undefined
         || input.active_scheduler !== undefined) ? input : null);
  if (!sv2 || typeof sv2 !== "object") return null;
  const entries = Array.isArray(sv2.entries)
    ? sv2.entries.filter((e) => e && e.source === "firmware_v2")
    : [];
  return {
    enabled: sv2.enabled === true,
    count: sv2.count != null ? sv2.count : entries.length,
    maxEntries: sv2.max_entries != null ? sv2.max_entries : null,
    executionEnabled: sv2.execution_enabled === true,   // sempre false na S2.5/6
    schedulerState: sv2.scheduler_state || "dormant",
    activeScheduler: sv2.active_scheduler || null,
    entries,
  };
}

// ============================================================================
// S2.9 — Fonte da verdade ÚNICA: device/snapshot > banco > frontend
// ============================================================================
/**
 * Resolve QUAL agenda o device realmente executa, a partir do snapshot.
 * Autoridade = device (snapshot). v2 só é "ativo" quando o firmware reporta
 * active_scheduler="v2" (ou version>=2 + execution_enabled). Caso contrário
 * (incl. firmware antigo/sem campos) → v1. NUNCA usa o banco como verdade.
 *
 * S2.9.1 — `activeSource` = executor REPORTADO pelo device. Para EXIBIÇÃO, se a
 * fonte ativa estiver vazia e a outra tiver entradas, `displaySource` cai na
 * fonte com entradas (e `fellBack=true`), e `active.{source,entries}` seguem
 * `displaySource` — assim a agenda real nunca fica escondida.
 *
 * Retorna { hasSnapshot, activeSource, displaySource, fellBack,
 *           active:{source,entries,enabled}, dormant,
 *           executionEnabled, schedulerState, version }.
 */
export function resolveActiveSchedule(snapshot, deviceData) {
  const hasSnapshot = !!snapshot && typeof snapshot === "object";
  const cap = getSchedulerCapability(deviceData || snapshot || {});
  const v1 = parseRealSchedule(snapshot || {});
  const v2 = parseScheduleV2(snapshot || {});

  const v2Active = !!(
    v2 && (v2.activeScheduler === "v2"
      || (cap.version >= 2 && v2.executionEnabled === true))
  );
  // Executor REPORTADO pelo device (autoridade do firmware) — inalterado.
  const activeSource = v2Active ? "v2" : "v1";

  // S2.9.1 — Fallback de EXIBIÇÃO. O executor reportado pode anunciar uma fonte
  // SEM entradas (ex.: active_scheduler="v2" com schedule_v2.entries=[]) enquanto
  // a agenda real está na OUTRA fonte (config_snapshot.schedule.entries, source
  // firmware_v1). Para não ESCONDER a agenda real: se a fonte ativa estiver vazia
  // e a outra tiver entradas, EXIBIMOS a que tem entradas. NÃO altera o que o
  // device executa — só o que a UI mostra. Ambas vazias → segue vazio. Fonte
  // ativa COM entradas → mantém (não quebra device genuinamente v2).
  const v1Entries = Array.isArray(v1.entries) ? v1.entries : [];
  const v2Entries = (v2 && Array.isArray(v2.entries)) ? v2.entries : [];
  let displaySource = activeSource;
  if (activeSource === "v2" && v2Entries.length === 0 && v1Entries.length > 0) {
    displaySource = "v1";
  } else if (activeSource === "v1" && v1Entries.length === 0 && v2Entries.length > 0) {
    displaySource = "v2";
  }
  const fellBack = displaySource !== activeSource;

  const active = displaySource === "v2"
    ? { source: "v2", entries: v2Entries, enabled: v2 ? v2.enabled : false }
    : { source: "v1", entries: v1Entries, enabled: v1.enabled };
  const dormant = displaySource === "v2"
    ? { source: "v1", entries: v1Entries }
    : (v2 ? { source: "v2", entries: v2Entries } : null);

  // Stopgap (snapshot atrasado) — sinal SEMPRE fresco vindo do heartbeat
  // (deviceData), independente do config_snapshot que pode atrasar ~10 hb após
  // edição LOCAL no Sonoff (/saveschedule não bumpa configRevision). Aceita
  // tanto deviceData.heartbeat (getDeviceCenter) quanto .last_hb_payload (DTO).
  const liveness = scheduleLiveness(
    (deviceData && (deviceData.heartbeat || deviceData.last_hb_payload)) || null,
  );

  return {
    hasSnapshot,
    activeSource,
    displaySource,
    fellBack,
    active,
    dormant,
    liveness,
    version: cap.version,
    executionEnabled: v2 ? v2.executionEnabled : false,
    schedulerState: v2 ? v2.schedulerState : (v2Active ? "active" : "v1"),
  };
}

// ============================================================================
// Stopgap — liveness da agenda via heartbeat (sempre fresco) + formatação
// ============================================================================
/**
 * Sinal de agenda ATIVA derivado do heartbeat (top-level do last_hb_payload):
 * scheduler_enabled / next_run / last_run. SEMPRE fresco (todo heartbeat), ao
 * contrário do config_snapshot, que pode atrasar ~10 hb após edição local.
 * Retorna { active, enabled, nextRun, lastRun } (epochs em segundos, 0 = sem).
 */
export function scheduleLiveness(hb) {
  const o = hb && typeof hb === "object" ? hb : {};
  const enabled = o.scheduler_enabled === true;
  const nextRun = Number(o.next_run) || 0;   // epoch s (UTC); 0 = sem horário
  const lastRun = Number(o.last_run) || 0;
  return { active: enabled || nextRun > 0, enabled, nextRun, lastRun };
}

/** Formata um epoch (segundos, UTC) do heartbeat em data/hora local pt-BR. */
export function fmtScheduleEpoch(epochS) {
  if (!epochS) return null;
  try {
    return new Date(epochS * 1000).toLocaleString("pt-BR", {
      day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return null;
  }
}

// Normaliza um horário/dias [+ ação opcional] em chave canônica. A DURAÇÃO
// fica FORA da chave (ADV-E): o desejado da aba (schedule_templates) não tem
// duration_s — será tratada quando o desejado vier de config_schedule entries.
// `action` só entra quando o caller pede (actionAware): v1 (reboot-only)
// mantém o legado time|days; v2/multi-event distingue relay_on×relay_off.
function _schedKey(time, days, action) {
  const t = (time || "").trim();
  const d = (Array.isArray(days) ? days.map(Number) : [])
    .filter((n) => n >= 0 && n <= 6).sort((a, b) => a - b).join(",");
  const base = `${t}|${d}`;
  return action ? `${base}|${action}` : base;
}

/**
 * S2.9 — Compara o DESEJADO (templates do banco) com o REAL (entries do
 * snapshot do device). APENAS INFORMATIVO — não corrige, não sincroniza.
 * Compara {time, dias} e, quando `options.actionAware` (ADV-E, p/ devices
 * v2/multi-event), também a `action` — evita falso "igual" entre relay_on e
 * relay_off no mesmo horário/dia. Duração fica fora da chave (ver _schedKey).
 * `actionAware` default = false → comportamento legado idêntico (v1).
 * Retorna { state: "synced"|"pending"|"divergent", confirmed: Set<key> }.
 */
export function compareScheduleSync(templates, realEntries, options = {}) {
  const actionAware = options.actionAware === true;
  const tplEnabled = (templates || []).filter((s) => s && s.enabled);
  const tplKeys = new Set(
    tplEnabled.map((s) => _schedKey(
      `${String(s.hour ?? 0).padStart(2, "0")}:${String(s.minute ?? 0).padStart(2, "0")}`,
      weekdayMaskToDays(s.weekday_mask),
      actionAware ? s.action : undefined,
    )),
  );
  const realKeys = new Set(
    (realEntries || []).map((e) => _schedKey(
      e.time, e.days, actionAware ? e.action : undefined,
    )),
  );

  let state;
  if (tplKeys.size === 0 && realKeys.size === 0) {
    state = "synced";
  } else if (realKeys.size === 0) {
    state = "pending";
  } else {
    let equal = tplKeys.size === realKeys.size;
    if (equal) for (const k of tplKeys) if (!realKeys.has(k)) { equal = false; break; }
    state = equal ? "synced" : "divergent";
  }
  return { state, confirmed: realKeys };
}

/**
 * Chave canônica de um template (para casar com `confirmed`). Usa a MESMA
 * regra de `_schedKey` que `compareScheduleSync` — incluindo `action` quando
 * `options.actionAware` (ADV-E); senão, legado time|days. Manter em sincronia
 * com compareScheduleSync, senão os chips "✓ no device" deixam de bater.
 */
export function templateSyncKey(s, options = {}) {
  return _schedKey(
    `${String(s.hour ?? 0).padStart(2, "0")}:${String(s.minute ?? 0).padStart(2, "0")}`,
    weekdayMaskToDays(s.weekday_mask),
    options.actionAware === true ? s.action : undefined,
  );
}
